import type { ServerConfig } from "@t3tools/contracts";

import { APP_BASE_NAME } from "./branding";
import { CLIENT_BUILD } from "./clientBuild";
import { isomorphicSessionStorage } from "./hooks/useLocalStorage";
import { t } from "~/i18n";

export interface VersionMismatch {
  readonly clientBuild: string;
  readonly serverBuild: string;
  readonly hint: string;
}

/**
 * The reload guard lives for the tab. Persisting it longer would suppress a
 * prompt when the same client build is deployed again.
 */
export const STALE_CLIENT_RELOAD_SESSION_KEY = "t3code:stale-client-reload:v1";

function normalizeVersion(version: string | null | undefined): string | null {
  const trimmed = version?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

export function resolveVersionMismatch(
  serverBuild: string | null | undefined,
): VersionMismatch | null {
  const normalizedClientBuild = normalizeVersion(CLIENT_BUILD);
  const normalizedServerBuild = normalizeVersion(serverBuild);
  if (
    !normalizedClientBuild ||
    !normalizedServerBuild ||
    normalizedClientBuild === normalizedServerBuild
  ) {
    return null;
  }

  return {
    clientBuild: normalizedClientBuild,
    serverBuild: normalizedServerBuild,
    hint: t("common/refreshThePageTo_ni54pw", { APP_BASE_NAME }),
  };
}

export function resolveServerConfigVersionMismatch(
  serverConfig: Pick<ServerConfig, "environment"> | null | undefined,
): VersionMismatch | null {
  return resolveVersionMismatch(serverConfig?.environment.clientBuild);
}

export function buildStaleClientKey(mismatch: Pick<VersionMismatch, "serverBuild">): string {
  return mismatch.serverBuild;
}

export function markStaleClientReloadRequested(staleKey: string): void {
  try {
    isomorphicSessionStorage.setItem(STALE_CLIENT_RELOAD_SESSION_KEY, staleKey);
  } catch {
    // Reload still proceeds; the guard only suppresses a repeat banner.
  }
}

export function wasStaleClientReloadRequested(staleKey: string): boolean {
  try {
    return isomorphicSessionStorage.getItem(STALE_CLIENT_RELOAD_SESSION_KEY) === staleKey;
  } catch {
    return false;
  }
}

export function appendVersionMismatchHint(
  message: string | null | undefined,
  mismatch: VersionMismatch | null | undefined,
): string | null {
  const normalizedMessage = normalizeVersion(message);
  if (!normalizedMessage) {
    return mismatch?.hint ?? null;
  }
  if (!mismatch) {
    return normalizedMessage;
  }
  return `${normalizedMessage} Hint: ${mismatch.hint}`;
}
